import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { currentBranding } from '../../../config/branding';
import { branding } from '../../../config/enum';
import {
  NewProgressProps as NewProgressProperties,
  SingleFileUploadProgress,
} from '../../../interface/file-upload-stream.interface';
import { AvailableStation, Amenity } from '../../../interface/station-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchApprovedStations } from '../../../redux/slice/ApprovedStationManagementSlice';
import {
  setUnUploadedFileData,
  updateFileUploadProgress,
} from '../../../redux/slice/FileUploadStreamSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { fetchRejectedStations } from '../../../redux/slice/RejectedStationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomAddressInput from '../../../shared-components/custom-address-input/CustomAddressInput';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomInputSwitch from '../../../shared-components/custom-input-switch/CustomInputSwitch';
import CustomMediaUpload from '../../../shared-components/custom-media-upload/CustomMediaUpload';
import CustomTimeRange from '../../../shared-components/custom-time-input/CustomTimeRange';
import FileUploadProgress from '../../../shared-components/file-upload-progress/FileUploadProgress';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SelectLocation from '../../../shared-components/select-location/SelectLocation';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotificaion';
import ConvertToUTC from '../../../shared-functions/ConvertToUTC';
import { SanitizeFileName } from '../../../shared-functions/StringFunctions';
import {
  checkEditFileSizeValidation,
  longTextValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import station from '../../../utils/api/station';

import SelectAmenities from './SelectAmenities';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Edit station component
 */
function EditStation() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { status, id } = useParams();
  const {
    stations: approvedStation,
    page: approvedPage,
    limit: approvedLimit,
  } = useAppSelector((state) => state.approvedStation);
  const {
    stations: rejectedStation,
    page: rejectedPage,
    limit: rejectedLimit,
  } = useAppSelector((state) => state.rejectStation);
  const [stationDetails, setStationDetails] = useState<AvailableStation>({
    id: '',
    _id: '',
    name: '',
    landOwnerShipDocument: null,
    editLandOwnerShipDocument: [],
    removedLandOwnerShipDocument: [],
    ebReport: null,
    editEbReport: [],
    removedEbReport: [],
    electricityContractType: '',
    opensAt: '',
    closesAt: '',
    address: '',
    latitude: null,
    longitude: null,
    medias: null,
    editMedias: [],
    removedMedias: [],
    amenities: [],
    electricityCostByUtilityProvider: null,
    isGuestChargingExclusive: false,
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  const [formStatus, setFormStatus] = useState({
    buttonLoader: false,
  });

  const listOfCountryCode = [
    {
      label: 'Standard Residential',
      value: 'standard-residential',
    },
    {
      label: 'Commercial',
      value: 'commercial',
    },
    {
      label: 'Industrial',
      value: 'industrial',
    },
    {
      label: 'EV Charging-Specific Contracts',
      value: 'ev-charging-specific-contracts',
    },
  ];

  /**
   * Handle input value change
   * @param name
   * @param changeValue
   */
  const handleStationDetailChange = (
    name: string,
    changeValue: string | number | File[] | Amenity[] | boolean,
  ) => {
    setStationDetails({
      ...stationDetails,
      [name]: changeValue,
    });
  };

  /**
   * Handle lat & long value change
   * @param changeValue
   */
  const handleChangeLatLong = (latitude: number, longitude: number) => {
    setStationDetails({
      ...stationDetails,
      latitude,
      longitude,
    });
  };

  /**
   * View amenities popup
   */
  const openAmenitiesModal = () => {
    setModalBoxDetail({
      title: 'Amenities',
      content: (
        <SelectAmenities
          selectedAmenities={stationDetails.amenities}
          handleCancel={() => setModalBoxStatus('close')}
          handleStationDetailChange={handleStationDetailChange}
        />
      ),
      notificationModal: false,
    });
    setModalBoxStatus('open');
  };

  /**
   * Function to get and handle file upload progress
   */
  const handleUploadProgress = (progress: NewProgressProperties) => {
    if (progress?.fileName && progress?.percentage) {
      dispatch(updateFileUploadProgress(progress));
    }
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = (fetchUpdatedData: boolean = false) => {
    setStationDetails({
      id: '',
      _id: '',
      name: '',
      landOwnerShipDocument: null,
      editLandOwnerShipDocument: [],
      removedLandOwnerShipDocument: [],
      ebReport: null,
      editEbReport: [],
      removedEbReport: [],
      electricityContractType: '',
      opensAt: '',
      closesAt: '',
      address: '',
      latitude: null,
      longitude: null,
      medias: null,
      editMedias: [],
      removedMedias: [],
      amenities: [],
      electricityCostByUtilityProvider: null,
      isGuestChargingExclusive: false,
    });
    setModalBoxStatus('close');

    navigate(`../${status}`);
    if (status === 'approved' && fetchUpdatedData) {
      dispatch(fetchApprovedStations({ page: approvedPage, limit: approvedLimit }));
    } else if (status === 'rejected' && fetchUpdatedData) {
      dispatch(fetchRejectedStations({ page: rejectedPage, limit: rejectedLimit }));
    }
  };

  /**
   * Show warning when navigate back
   */
  const handleClickCancel = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title="Are you sure want to Cancel?"
          description="Any unsaved changes will be loss"
          cancelButtonLabel="Cancel"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel="OK"
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Update station API call
   */
  const handleSubmitStationDetails = () => {
    if (
      stringValidation(1, 100, stationDetails.name) &&
      (currentBranding === branding.VOLTIE ||
        checkEditFileSizeValidation(stationDetails.editLandOwnerShipDocument, 5, 1)) &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.landOwnerShipDocument?.length || 0) +
          (stationDetails.editLandOwnerShipDocument?.length || 0) ===
          1) &&
      (currentBranding === branding.VOLTIE ||
        checkEditFileSizeValidation(stationDetails.editEbReport, 5, 1)) &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.ebReport?.length || 0) + (stationDetails.editEbReport?.length || 0) ===
          1) &&
      // (stationDetails.landOwnerShipDocument?.length || 0) +
      //   (stationDetails.editLandOwnerShipDocument?.length || 0) ===
      //   1 &&
      // (currentBranding === branding.VOLTIE ||
      //   checkEditFileSizeValidation(stationDetails.editLandOwnerShipDocument, 5, 0)) &&
      // (currentBranding === branding.VOLTIE ||
      //   (stationDetails.ebReport?.length || 0) + (stationDetails.editEbReport?.length || 0) === 1 ||
      //   // (stationDetails.editEbReport?.length === 1 &&
      //   checkEditFileSizeValidation(stationDetails.editEbReport, 5, 0)) &&
      stationDetails.electricityContractType &&
      (currentBranding === branding.VOLTIE ||
        (stationDetails.opensAt && stationDetails.closesAt)) &&
      longTextValidation(500, stationDetails.address) &&
      stationDetails.latitude &&
      stationDetails.longitude &&
      (stationDetails.medias?.length || 0) + (stationDetails.editMedias?.length || 0) < 10 &&
      (stationDetails.editMedias?.length === 1 || currentBranding === branding.VOLTIE) &&
      checkEditFileSizeValidation(stationDetails.editMedias, 5, 1) &&
      stationDetails.electricityCostByUtilityProvider &&
      stationDetails.electricityCostByUtilityProvider > 0
      // stationDetails.amenities?.length > 0
    ) {
      setFormStatus({
        buttonLoader: true,
      });
      const formData = new FormData();

      // Append simple fields
      formData.append('name', stationDetails.name);
      formData.append('electricityContractType', stationDetails.electricityContractType);

      formData.append('address', stationDetails.address);
      formData.append(
        'electricityCostByUtilityProvider',
        stationDetails.electricityCostByUtilityProvider.toString(),
      );
      formData.append(
        'isGuestChargingExclusive',
        stationDetails.isGuestChargingExclusive.toString(),
      );
      formData.append(
        'latitude',
        stationDetails.latitude ? stationDetails.latitude.toString() : '',
      );
      formData.append(
        'longitude',
        stationDetails.longitude ? stationDetails.longitude.toString() : '',
      );

      if (stationDetails?.opensAt) {
        formData.append('opensAt', ConvertToUTC(stationDetails.opensAt));
      }

      if (stationDetails?.closesAt) {
        formData.append('closesAt', ConvertToUTC(stationDetails.closesAt));
      }

      // Append files for landOwnerShipDocument if it's an array
      if (Array.isArray(stationDetails.editLandOwnerShipDocument) && status !== 'approved') {
        stationDetails.editLandOwnerShipDocument.forEach((file) => {
          formData.append(`landOwnerShipDocument`, file);
        });
      }

      // Append files for ebReport if it's an array
      if (Array.isArray(stationDetails.editEbReport) && status !== 'approved') {
        stationDetails.editEbReport.forEach((file) => {
          formData.append(`ebReport`, file);
        });
      }

      // Append multiple files for medias (if it's an array)
      if (stationDetails.editMedias && Array.isArray(stationDetails.editMedias)) {
        stationDetails.editMedias.forEach((media) => {
          formData.append(`medias`, media);
        });
      }

      // Append multiple amenities
      if (stationDetails.amenities && Array.isArray(stationDetails.amenities)) {
        stationDetails.amenities.forEach((amenity, index) => {
          formData.append(`amenities[${index}]`, amenity);
        });
      }

      // RemovedURLs
      let removedURL: string[] = [
        // ...(stationDetails.removedLandOwnerShipDocument || []),
        // ...(stationDetails.removedEbReport || []),
        ...(stationDetails.removedMedias || []),
      ];

      if (status !== 'approved' && stationDetails.removedEbReport) {
        removedURL = [...removedURL, ...stationDetails.removedEbReport];
      } else if (status === 'approved' && stationDetails.removedEbReport.length > 0) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: 400,
              message: 'Eb-report cannot be edited for approved stations',
            },
          }),
        );
        setFormStatus({
          buttonLoader: false,
        });
        return;
      }

      if (status !== 'approved' && stationDetails.removedLandOwnerShipDocument) {
        removedURL = [...removedURL, ...stationDetails.removedLandOwnerShipDocument];
      } else if (status === 'approved' && stationDetails.removedLandOwnerShipDocument.length > 0) {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: 400,
              message: 'Landownership documents cannot be edited for approved stations',
            },
          }),
        );
        setFormStatus({
          buttonLoader: false,
        });
        return;
      }

      if (removedURL.length > 0) {
        if (removedURL?.length) {
          removedURL.forEach((url, index) => {
            formData.append(`removedUrls[${index}]`, url);
          });
        }
      }

      const binaryFileData: SingleFileUploadProgress[] = [
        ...(stationDetails.editLandOwnerShipDocument || []),
        ...(stationDetails.editEbReport || []),
        ...(stationDetails.editMedias || []),
      ]?.map((fileData) => ({
        fileName: SanitizeFileName(fileData.name),
        progress: 0,
        status: 'pending',
      }));

      if (binaryFileData?.length) {
        dispatch(setUnUploadedFileData(binaryFileData));
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: 'File',
          content: <FileUploadProgress />,
          notificationModal: false,
        });
      }

      station
        .editStation(stationDetails.id, formData, handleUploadProgress)
        .then(() => {
          setModalBoxStatus('open');
          setFormStatus({
            buttonLoader: false,
          });
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title="Station edited Successfully "
                description="Your Station is edited successfully"
                notificationCloseTimer={3000}
                onNotificationClose={() => handleNavigateBack(true)}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setModalBoxStatus('close');
          setFormStatus({
            buttonLoader: false,
          });
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    }
  };

  useEffect(() => {
    let selectedStation: AvailableStation | undefined = stationDetails;
    if (status === 'approved') {
      selectedStation = approvedStation.find((eachStation) => eachStation.id === id);
    } else if (status === 'rejected') {
      selectedStation = rejectedStation.find((eachStation) => eachStation.id === id);
    }
    if (selectedStation) {
      setStationDetails((previousValue) => ({
        ...previousValue,
        ...selectedStation,
      }));
    }
  }, [status, id]);

  /**
   * Handle remove existing document
   * @param url
   */
  const handleRemoveExistingFile = (
    name: string,
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => {
    if (name === 'landOwnerShipDocument' && type === 'existingFile') {
      setStationDetails((previousValue) => {
        if (!previousValue) {
          return previousValue;
        }

        return {
          ...previousValue,
          landOwnerShipDocument: (previousValue.landOwnerShipDocument || []).filter(
            (data) => data.url !== url,
          ),
          removedLandOwnerShipDocument: [...previousValue.removedLandOwnerShipDocument, url],
        };
      });
    } else if (name === 'editLandOwnerShipDocument' && type === 'newFile') {
      setStationDetails((previousValue) => {
        if (!previousValue) {
          return previousValue;
        }

        return {
          ...previousValue,
          editLandOwnerShipDocument: (previousValue.editLandOwnerShipDocument || []).filter(
            (_, index) => index !== indexToRemove,
          ),
        };
      });
    } else if (name === 'ebReport' && type === 'existingFile') {
      setStationDetails((previousValue) => {
        if (!previousValue) {
          return previousValue;
        }

        return {
          ...previousValue,
          ebReport: (previousValue.ebReport || []).filter((data) => data.url !== url),
          removedEbReport: [...previousValue.removedEbReport, url],
        };
      });
    } else if (name === 'editEbReport' && type === 'newFile') {
      setStationDetails((previousValue) => {
        if (!previousValue) {
          return previousValue;
        }

        return {
          ...previousValue,
          editEbReport: (previousValue.editEbReport || []).filter(
            (_, index) => index !== indexToRemove,
          ),
        };
      });
    } else {
      console.warn('Unexpected name or type:', name, type);
    }
  };

  /**
   * Handle remove existing media
   * @param name
   * @param originalIndex
   * @param url
   * @param type
   */
  const handleRemoveMedias = (
    name: string,
    originalIndex: number,
    url: string,
    type: 'newFile' | 'existingFile',
  ) => {
    if (type === 'newFile') {
      setStationDetails((previousValue) => {
        if (!previousValue) {
          return previousValue;
        }

        return {
          ...previousValue,
          editMedias: (previousValue.editMedias || []).filter(
            (_, index) => index !== originalIndex,
          ),
        };
      });
    } else if (type === 'existingFile') {
      setStationDetails((previousValue) => {
        if (!previousValue) {
          return previousValue;
        }

        return {
          ...previousValue,
          medias: (previousValue.medias || []).filter((data) => data.url !== url),
          removedMedias: [...previousValue.removedMedias, url],
        };
      });
    }
  };

  /**
   * Handle Change address
   */
  const handleChangeAddress = (
    name: string,
    address: string | number,
    latitude: number | null,
    longitude: number | null,
  ) => {
    setStationDetails({
      ...stationDetails,
      [name]: address,
      latitude,
      longitude,
    });
  };

  return (
    <div className="add-station-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="add-station-header">
        <PageTitle title="Edit Station" />
      </div>
      <div className="add-station-container">
        <div className="add-station-from-wrap">
          <div className="add-station-from">
            <CustomInput
              inputLabel="Station Name"
              type="email"
              placeholder="Enter the Name"
              name="name"
              value={stationDetails.name}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={!stringValidation(1, 100, stationDetails.name)}
              errorMessage="Station name is required"
            />
            <CustomFileInput
              inputLabel="Land Ownership Document"
              acceptedFileType="image/*, application/pdf"
              placeholder="Upload pdf, jpeg"
              name="editLandOwnerShipDocument"
              selectedFiles={stationDetails.editLandOwnerShipDocument}
              onChangeValue={handleStationDetailChange}
              existingName="landOwnerShipDocument"
              existingFiles={stationDetails.landOwnerShipDocument}
              unselectHandler={handleRemoveExistingFile}
              valueRequired={currentBranding !== branding.VOLTIE}
              error={
                !(
                  (currentBranding === branding.VOLTIE ||
                    checkEditFileSizeValidation(stationDetails.editLandOwnerShipDocument, 5, 1)) &&
                  (currentBranding === branding.VOLTIE ||
                    (stationDetails.landOwnerShipDocument?.length || 0) +
                      (stationDetails.editLandOwnerShipDocument?.length || 0) ===
                      1)
                )
              }
              errorMessage="Document is required and maximum of only one can be uploaded"
              readOnly={currentBranding !== branding.VOLTIE ? status === 'approved' : false}
            />
            <CustomFileInput
              inputLabel="EB Report of past 6 months"
              acceptedFileType="application/pdf"
              placeholder="Upload pdf"
              name="editEbReport"
              selectedFiles={stationDetails.editEbReport}
              onChangeValue={handleStationDetailChange}
              existingName="ebReport"
              existingFiles={stationDetails.ebReport}
              unselectHandler={handleRemoveExistingFile}
              valueRequired={currentBranding !== branding.VOLTIE}
              error={
                !(
                  (currentBranding === branding.VOLTIE ||
                    checkEditFileSizeValidation(stationDetails.editEbReport, 5, 1)) &&
                  (currentBranding === branding.VOLTIE ||
                    (stationDetails.ebReport?.length || 0) +
                      (stationDetails.editEbReport?.length || 0) ===
                      1)
                )
              }
              errorMessage="Document is required and maximum of only one can be uploaded"
              readOnly={currentBranding !== branding.VOLTIE ? status === 'approved' : false}
            />
            <SelectFiled
              inputLabel="Electricity Contract type"
              name="electricityContractType"
              placeholder="Choose Electricity Contract type"
              selectedValue={stationDetails.electricityContractType}
              onChangeValue={handleStationDetailChange}
              options={listOfCountryCode}
              valueRequired
              error={!stationDetails.electricityContractType}
              errorMessage="Station name is required"
            />
            <CustomAddressInput
              inputLabel="Address"
              placeholder="Enter the address"
              name="address"
              value={stationDetails.address}
              onChangeValue={handleChangeAddress}
              valueRequired
              error={!longTextValidation(500, stationDetails.address)}
              errorMessage="Address is required"
            />
            <SelectLocation
              inputLabel="Latitude & Longitude"
              latitude={stationDetails.latitude}
              longitude={stationDetails.longitude}
              onChangeValue={handleChangeLatLong}
              valueRequired
              error={!(stationDetails.latitude && stationDetails.longitude)}
              errorMessage="Latitude and Longitude is required"
            />
            <CustomTimeRange
              name1="opensAt"
              name2="closesAt"
              inputLabel="Operating Hours"
              value1={stationDetails.opensAt}
              value2={stationDetails.closesAt}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={!(stationDetails.opensAt && stationDetails.closesAt)}
              // errorMessage="Invalid time range"
            />
            <CustomMediaUpload
              inputLabel="Upload Media"
              acceptedFileType="image/* video/*"
              placeholder="Upload jpeg, png, mp4, avi"
              name="editMedias"
              selectedFiles={stationDetails.editMedias}
              onChangeValue={handleStationDetailChange}
              existingName="medias"
              existingFiles={stationDetails.medias}
              unselectHandler={handleRemoveMedias}
              valueRequired={currentBranding !== branding.VOLTIE}
              error={
                currentBranding !== branding.VOLTIE &&
                ((stationDetails.medias?.length || 0) + (stationDetails.editMedias?.length || 0) ===
                  0 ||
                  !checkEditFileSizeValidation(stationDetails.editMedias, 5, 10))
              }
              errorMessage="Document is required and maximum of only one can be uploaded"
            />
            <CustomInput
              inputLabel="Electricity Price (per Kwh)"
              type="number"
              placeholder="Enter Electricity Price"
              name="electricityCostByUtilityProvider"
              value={stationDetails.electricityCostByUtilityProvider}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={
                !(
                  stationDetails.electricityCostByUtilityProvider &&
                  stationDetails.electricityCostByUtilityProvider > 0
                )
              }
              errorMessage="Electricity price is required"
            />
            <CustomInputSwitch
              inputLabel="Exclusive for guest charging"
              value={stationDetails.isGuestChargingExclusive}
              name="isGuestChargingExclusive"
              onChangeValue={handleStationDetailChange}
            />
          </div>
        </div>
        <div className="add-station-button-wrap">
          <div className="amenities-button">
            <MyButton
              label="Amenities"
              svgIcon={<PlusIcon />}
              // buttonType={stationDetails.amenities.length === 0 ? 'error' : 'back'}
              buttonType="back"
              disableHoverEffect
              onClickFunc={openAmenitiesModal}
              notifyCount={stationDetails?.amenities?.length || 0}
            />
          </div>
          <div className="form-buttons-wrap">
            <MyButton label="Cancel" buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label="Submit"
              buttonType="submit"
              onClickFunc={handleSubmitStationDetails}
              disableHoverEffect
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditStation;
